import { useState, useEffect } from "react";
import axios from "axios";
import { 
  X, 
  Mail, 
  FileText, 
  Users, 
  BarChart3, 
  Plus, 
  Loader2, 
  Send, 
  CheckCircle2,
  XCircle,
  Eye,
  Code,
  Type,
  Image,
  Video,
  MousePointer,
  Gift,
  List,
  Minus,
  Save,
  Trash2,
  Edit2,
  MapPin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Template variable tags
const VARIABLE_TAGS = [
  { key: "business_name", label: "{business_name}" },
  { key: "location", label: "{location}" },
  { key: "niche", label: "{niche}" },
  { key: "website", label: "{website}" },
  { key: "sender_name", label: "{sender_name}" },
  { key: "sender_company", label: "{sender_company}" },
];

// Helper to generate YouTube thumbnail HTML with play button
const generateVideoThumbnailHTML = (youtubeUrl) => {
  // Extract video ID from various YouTube URL formats
  let videoId = null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /^([a-zA-Z0-9_-]{11})$/ // Just the ID
  ];
  
  for (const pattern of patterns) {
    const match = youtubeUrl.match(pattern);
    if (match) {
      videoId = match[1];
      break;
    }
  }
  
  if (!videoId) return null;
  
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  const videoUrl = `https://www.youtube.com/watch?v=${videoId}`;
  
  // Simple, email-safe HTML that works everywhere
  return `
<table cellpadding="0" cellspacing="0" border="0" width="480" style="max-width: 100%;">
  <tr>
    <td align="center">
      <a href="${videoUrl}" target="_blank" style="text-decoration: none; display: block;">
        <img src="${thumbnailUrl}" alt="Click to watch video" width="480" height="360" style="display: block; max-width: 100%; height: auto; border: 2px solid #cc0000; border-radius: 4px;" />
      </a>
    </td>
  </tr>
  <tr>
    <td align="center" style="padding: 12px 0;">
      <a href="${videoUrl}" target="_blank" style="display: inline-block; background-color: #cc0000; color: #ffffff; padding: 12px 24px; font-size: 14px; font-weight: bold; text-decoration: none; border-radius: 4px; font-family: Arial, sans-serif;">▶ WATCH VIDEO</a>
    </td>
  </tr>
</table>`.trim();
};

const BulkEmailCampaign = ({ isOpen, onClose, businesses, stats }) => {
  const [activeTab, setActiveTab] = useState("templates");
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editMode, setEditMode] = useState("visual"); // "visual" or "html"
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Project state
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    subject: "",
    html_content: ""
  });
  
  // Send email state
  const [senderName, setSenderName] = useState("");
  const [senderCompany, setSenderCompany] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [senderEmails, setSenderEmails] = useState([]);
  const [selectedSenderEmail, setSelectedSenderEmail] = useState("");
  const [showVideoInput, setShowVideoInput] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  
  // Calculate leads with emails
  const leadsWithEmail = businesses.filter(b => b.email);
  
  useEffect(() => {
    if (isOpen) {
      fetchProjects();
      fetchTemplates();
      fetchSenderEmails();
    }
  }, [isOpen]);
  
  // Re-fetch campaigns when project changes
  useEffect(() => {
    if (isOpen) {
      fetchCampaigns();
    }
  }, [isOpen, selectedProject]);
  
  // Auto-select sender email when project changes
  useEffect(() => {
    if (selectedProject) {
      const proj = projects.find(p => p.id === selectedProject);
      if (proj) {
        setSelectedSenderEmail(proj.sender);
      }
    }
  }, [selectedProject, projects]);
  
  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data.projects);
      if (!selectedProject && response.data.projects.length > 0) {
        setSelectedProject(response.data.projects[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    }
  };
  
  const fetchSenderEmails = async () => {
    try {
      const response = await axios.get(`${API}/sender-emails`);
      setSenderEmails(response.data.sender_emails);
      setSelectedSenderEmail(response.data.default);
    } catch (error) {
      console.error("Failed to fetch sender emails:", error);
    }
  };
  
  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
      toast.error("Failed to fetch templates");
    }
  };
  
  const fetchCampaigns = async () => {
    try {
      const params = selectedProject ? `?project=${selectedProject}` : '';
      const response = await axios.get(`${API}/campaigns${params}`);
      setCampaigns(response.data);
    } catch (error) {
      console.error("Failed to fetch campaigns:", error);
    }
  };
  
  const handleCreateTemplate = async () => {
    if (!formData.name || !formData.subject || !formData.html_content) {
      toast.error("Please fill in template name, subject, and email body");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/templates`, formData);
      setTemplates([response.data, ...templates]);
      setShowCreateForm(false);
      setFormData({ name: "", description: "", subject: "", html_content: "" });
      toast.success("Template created successfully!");
    } catch (error) {
      console.error("Failed to create template:", error);
      toast.error("Failed to create template");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteTemplate = async (templateId) => {
    if (!confirm("Are you sure you want to delete this template?")) return;
    
    try {
      await axios.delete(`${API}/templates/${templateId}`);
      setTemplates(templates.filter(t => t.id !== templateId));
      toast.success("Template deleted");
    } catch (error) {
      console.error("Failed to delete template:", error);
      toast.error("Failed to delete template");
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || "",
      subject: template.subject,
      html_content: template.html_content
    });
  };

  const handleUpdateTemplate = async () => {
    if (!formData.name || !formData.subject || !formData.html_content) {
      toast.error("Please fill in template name, subject, and email body");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.put(`${API}/templates/${editingTemplate.id}`, formData);
      setTemplates(templates.map(t => t.id === editingTemplate.id ? response.data : t));
      setEditingTemplate(null);
      setFormData({ name: "", description: "", subject: "", html_content: "" });
      toast.success("Template updated successfully!");
    } catch (error) {
      console.error("Failed to update template:", error);
      toast.error("Failed to update template");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSendTestEmail = async () => {
    if (!selectedTemplate || !testEmail) {
      toast.error("Please select a template and enter a test email");
      return;
    }
    
    setIsSending(true);
    try {
      await axios.post(`${API}/email/send-test`, {
        template_id: selectedTemplate.id,
        test_email: testEmail,
        sender_name: senderName || "Your Name",
        sender_company: senderCompany || "Your Company",
        sender_email: selectedSenderEmail
      });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      console.error("Failed to send test email:", error);
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setIsSending(false);
    }
  };
  
  const handleSendBulkEmail = async () => {
    if (!selectedTemplate) {
      toast.error("Please select a template first");
      return;
    }
    
    if (leadsWithEmail.length === 0) {
      toast.error("No leads with email addresses found");
      return;
    }
    
    if (!senderName || !senderCompany) {
      toast.error("Please enter your name and company");
      return;
    }
    
    const payload = {
      template_id: selectedTemplate.id,
      businesses: leadsWithEmail.map(b => ({
        id: b.id,
        name: b.name,
        email: b.email,
        address: b.address,
        category: b.category,
        website: b.website
      })),
      sender_name: senderName,
      sender_company: senderCompany,
      sender_email: selectedSenderEmail,
      project: selectedProject
    };
    
    // Step 1: Check for duplicates first
    setIsSending(true);
    try {
      const checkResponse = await axios.post(`${API}/email/check-duplicates`, payload);
      const { new_count, duplicate_count, total } = checkResponse.data;
      
      if (new_count === 0) {
        toast.error(`All ${total} emails have already been sent — no new recipients.`);
        setIsSending(false);
        return;
      }
      
      let confirmMsg = `Send ${new_count} emails?`;
      if (duplicate_count > 0) {
        confirmMsg = `${duplicate_count} of ${total} are duplicates (already sent). Send to the remaining ${new_count} new recipients?`;
      }
      
      if (!confirm(confirmMsg)) {
        setIsSending(false);
        return;
      }
      
      // Step 2: Actually send
      const response = await axios.post(`${API}/email/send-bulk`, payload);
      
      let message = `Sent ${response.data.successful} emails successfully!`;
      if (response.data.duplicates_skipped > 0) {
        message += ` (${response.data.duplicates_skipped} duplicates skipped)`;
      }
      toast.success(message);
      fetchCampaigns();
      setActiveTab("results");
    } catch (error) {
      console.error("Failed to send bulk email:", error);
      toast.error(error.response?.data?.detail || "Failed to send bulk emails");
    } finally {
      setIsSending(false);
    }
  };
  
  const insertVariable = (variable) => {
    setFormData(prev => ({
      ...prev,
      html_content: prev.html_content + `{${variable}}`
    }));
  };

  const insertVideoThumbnail = () => {
    if (!videoUrl) {
      toast.error("Please enter a YouTube URL");
      return;
    }
    
    const html = generateVideoThumbnailHTML(videoUrl);
    if (html) {
      setFormData(prev => ({
        ...prev,
        html_content: prev.html_content + "\n\n" + html + "\n\n"
      }));
      setVideoUrl("");
      setShowVideoInput(false);
      toast.success("Video thumbnail added!");
    } else {
      toast.error("Invalid YouTube URL. Use format: https://youtube.com/watch?v=VIDEO_ID");
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-green-500" />
            <h2 className="text-lg font-mono font-bold text-white uppercase tracking-wider">
              Email Campaign
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {/* Project Selector */}
            {projects.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-zinc-500 uppercase">Project:</span>
                <select
                  data-testid="project-selector"
                  value={selectedProject || ''}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 text-white font-mono text-sm focus:outline-none focus:border-green-500"
                >
                  {projects.map((proj) => (
                    <option key={proj.id} value={proj.id}>
                      {proj.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-zinc-800 transition-colors"
              data-testid="close-bulk-email"
            >
              <X className="w-5 h-5 text-zinc-500" />
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-zinc-800">
          <button
            onClick={() => setActiveTab("templates")}
            className={`flex-1 px-4 py-3 text-sm font-mono uppercase tracking-wider flex items-center justify-center gap-2 transition-colors ${
              activeTab === "templates"
                ? "text-green-500 border-b-2 border-green-500 bg-green-500/5"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <FileText className="w-4 h-4" />
            Templates
          </button>
          <button
            onClick={() => setActiveTab("drafts")}
            className={`flex-1 px-4 py-3 text-sm font-mono uppercase tracking-wider flex items-center justify-center gap-2 transition-colors ${
              activeTab === "drafts"
                ? "text-green-500 border-b-2 border-green-500 bg-green-500/5"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <Users className="w-4 h-4" />
            Drafts ({0})
          </button>
          <button
            onClick={() => setActiveTab("results")}
            className={`flex-1 px-4 py-3 text-sm font-mono uppercase tracking-wider flex items-center justify-center gap-2 transition-colors ${
              activeTab === "results"
                ? "text-green-500 border-b-2 border-green-500 bg-green-500/5"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Results
          </button>
          <button
            onClick={() => setActiveTab("locations")}
            className={`flex-1 px-4 py-3 text-sm font-mono uppercase tracking-wider flex items-center justify-center gap-2 transition-colors ${
              activeTab === "locations"
                ? "text-green-500 border-b-2 border-green-500 bg-green-500/5"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <MapPin className="w-4 h-4" />
            Locations
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Stats Bar */}
          <ProjectStatsBar 
            leadsWithEmail={leadsWithEmail} 
            businesses={businesses} 
            selectedProject={selectedProject}
            activeTab={activeTab}
            onNewTemplate={() => setShowCreateForm(true)}
          />
          
          {/* Templates Tab */}
          {activeTab === "templates" && !showCreateForm && !editingTemplate && (
            <div>
              {templates.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500 font-mono">No templates yet. Create one to get started.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className={`p-4 border transition-colors cursor-pointer ${
                        selectedTemplate?.id === template.id
                          ? "border-green-500 bg-green-500/10"
                          : "border-zinc-700 hover:border-zinc-600"
                      }`}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-mono font-bold text-white">{template.name}</h3>
                          <p className="text-sm text-zinc-500">{template.description}</p>
                          <p className="text-xs text-zinc-600 mt-1">Subject: {template.subject}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditTemplate(template);
                            }}
                            className="p-2 hover:bg-blue-500/20 text-zinc-500 hover:text-blue-400 transition-colors"
                            title="Edit template"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTemplate(template.id);
                            }}
                            className="p-2 hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-colors"
                            title="Delete template"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Send Controls */}
                  {selectedTemplate && (
                    <div className="mt-6 p-4 bg-zinc-800/50 border border-zinc-700 space-y-4">
                      <h4 className="font-mono text-sm text-green-500 uppercase">Send Email</h4>
                      
                      {/* Sender Email - Auto-set by project */}
                      <div>
                        <Label className="text-xs font-mono text-zinc-500">Sending From</Label>
                        <div className="mt-1 p-2 bg-zinc-900 border border-zinc-700 text-green-400 font-mono text-sm flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          {selectedSenderEmail}
                          {selectedProject && (
                            <span className="text-xs text-zinc-500 ml-auto">({selectedProject})</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs font-mono text-zinc-500">Your Name</Label>
                          <Input
                            value={senderName}
                            onChange={(e) => setSenderName(e.target.value)}
                            placeholder="John Smith"
                            className="mt-1 bg-zinc-900 border-zinc-700 rounded-none h-10 font-mono"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-mono text-zinc-500">Your Company</Label>
                          <Input
                            value={senderCompany}
                            onChange={(e) => setSenderCompany(e.target.value)}
                            placeholder="Acme Web Services"
                            className="mt-1 bg-zinc-900 border-zinc-700 rounded-none h-10 font-mono"
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-end gap-4">
                        <div className="flex-1">
                          <Label className="text-xs font-mono text-zinc-500">Test Email</Label>
                          <Input
                            type="email"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            placeholder="your@email.com"
                            className="mt-1 bg-zinc-900 border-zinc-700 rounded-none h-10 font-mono"
                          />
                        </div>
                        <Button
                          onClick={handleSendTestEmail}
                          disabled={isSending || !testEmail}
                          variant="outline"
                          className="rounded-none border-zinc-700 hover:bg-zinc-800 font-mono text-xs"
                        >
                          {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                          Send Test
                        </Button>
                      </div>
                      
                      <Button
                        onClick={handleSendBulkEmail}
                        disabled={isSending || leadsWithEmail.length === 0}
                        className="w-full bg-green-500 hover:bg-green-600 text-black font-mono rounded-none"
                      >
                        {isSending ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send to All {leadsWithEmail.length} Leads
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Create Template Form */}
          {activeTab === "templates" && showCreateForm && (
            <div className="space-y-4">
              <h3 className="text-green-500 font-mono uppercase tracking-wider">Create New Template</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono text-zinc-500">Template Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Web Development Pitch"
                    className="mt-1 bg-zinc-900 border-zinc-700 rounded-none h-10 font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono text-zinc-500">Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Short description"
                    className="mt-1 bg-zinc-900 border-zinc-700 rounded-none h-10 font-mono"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-xs font-mono text-zinc-500">Subject Line *</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="e.g., Website services for {business_name}"
                  className="mt-1 bg-zinc-900 border-zinc-700 rounded-none h-10 font-mono"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-mono text-zinc-500">Email Body (HTML) *</Label>
                  <div className="flex items-center gap-1">
                    {VARIABLE_TAGS.map((tag) => (
                      <button
                        key={tag.key}
                        onClick={() => insertVariable(tag.key)}
                        className="px-2 py-1 text-xs font-mono bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white transition-colors"
                      >
                        {tag.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => setEditMode("visual")}
                    className={`flex items-center gap-1 px-3 py-1.5 text-xs font-mono transition-colors ${
                      editMode === "visual"
                        ? "bg-green-500/20 text-green-400 border border-green-500/50"
                        : "bg-zinc-800 text-zinc-400 border border-zinc-700"
                    }`}
                  >
                    <Eye className="w-3 h-3" />
                    Visual Builder
                  </button>
                  <button
                    onClick={() => setEditMode("html")}
                    className={`flex items-center gap-1 px-3 py-1.5 text-xs font-mono transition-colors ${
                      editMode === "html"
                        ? "bg-green-500/20 text-green-400 border border-green-500/50"
                        : "bg-zinc-800 text-zinc-400 border border-zinc-700"
                    }`}
                  >
                    <Code className="w-3 h-3" />
                    HTML Code
                  </button>
                </div>
                
                {/* Visual Builder Toolbar */}
                {editMode === "visual" && (
                  <div className="flex flex-col gap-2 mb-2 p-2 bg-zinc-800/50 border border-zinc-700">
                    <div className="flex items-center gap-2">
                      <button className="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                        <Type className="w-3 h-3" /> Text
                      </button>
                      <button className="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                        <Image className="w-3 h-3" /> Image
                      </button>
                      <button 
                        onClick={() => setShowVideoInput(!showVideoInput)}
                        className={`flex items-center gap-1 px-2 py-1 text-xs font-mono ${showVideoInput ? 'bg-red-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                      >
                        <Video className="w-3 h-3" /> Video
                      </button>
                      <button className="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                        <MousePointer className="w-3 h-3" /> Button
                      </button>
                      <button className="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                        <Gift className="w-3 h-3" /> Promo Box
                      </button>
                      <button className="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                        <List className="w-3 h-3" /> Bullet List
                      </button>
                      <button className="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                        <Minus className="w-3 h-3" /> Divider
                      </button>
                    </div>
                    
                    {/* Video URL Input */}
                    {showVideoInput && (
                      <div className="flex items-center gap-2 p-2 bg-red-900/20 border border-red-500/30">
                        <Video className="w-4 h-4 text-red-400" />
                        <input
                          type="text"
                          value={videoUrl}
                          onChange={(e) => setVideoUrl(e.target.value)}
                          placeholder="Paste YouTube URL (e.g., https://youtube.com/watch?v=...)"
                          className="flex-1 px-2 py-1 bg-zinc-900 border border-zinc-700 text-white text-xs font-mono focus:outline-none focus:border-red-500"
                        />
                        <button
                          onClick={insertVideoThumbnail}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-mono"
                        >
                          Insert Thumbnail
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                <textarea
                  value={formData.html_content}
                  onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                  placeholder={editMode === "html" 
                    ? "<html>\n<body>\n<p>Hello {business_name},</p>\n<p>Your email content here...</p>\n</body>\n</html>"
                    : "Click buttons above to add content blocks..."
                  }
                  className="w-full h-64 p-3 bg-zinc-900 border border-zinc-700 text-white font-mono text-sm resize-none focus:outline-none focus:border-green-500"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleCreateTemplate}
                  disabled={isLoading}
                  className="bg-green-500 hover:bg-green-600 text-black font-mono rounded-none"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Template
                </Button>
                <Button
                  onClick={() => {
                    setShowCreateForm(false);
                    setFormData({ name: "", description: "", subject: "", html_content: "" });
                  }}
                  variant="outline"
                  className="rounded-none border-zinc-700 font-mono"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Edit Template Form */}
          {activeTab === "templates" && editingTemplate && (
            <div className="space-y-4">
              <h3 className="text-blue-500 font-mono uppercase tracking-wider">Edit Template</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono text-zinc-500">Template Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Web Development Pitch"
                    className="mt-1 bg-zinc-900 border-zinc-700 rounded-none h-10 font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono text-zinc-500">Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Short description"
                    className="mt-1 bg-zinc-900 border-zinc-700 rounded-none h-10 font-mono"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-xs font-mono text-zinc-500">Subject Line *</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="e.g., Website services for {business_name}"
                  className="mt-1 bg-zinc-900 border-zinc-700 rounded-none h-10 font-mono"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-mono text-zinc-500">Email Body (HTML) *</Label>
                  <div className="flex items-center gap-1">
                    {VARIABLE_TAGS.map((tag) => (
                      <button
                        key={tag.key}
                        onClick={() => insertVariable(tag.key)}
                        className="px-2 py-1 text-xs font-mono bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white transition-colors"
                      >
                        {tag.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Visual Builder / HTML Code Toggle */}
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => setEditMode("visual")}
                    className={`flex items-center gap-1 px-3 py-1.5 text-xs font-mono transition-colors ${
                      editMode === "visual"
                        ? "bg-blue-500/20 text-blue-400 border border-blue-500/50"
                        : "bg-zinc-800 text-zinc-400 border border-zinc-700"
                    }`}
                  >
                    <Eye className="w-3 h-3" />
                    Visual Builder
                  </button>
                  <button
                    onClick={() => setEditMode("html")}
                    className={`flex items-center gap-1 px-3 py-1.5 text-xs font-mono transition-colors ${
                      editMode === "html"
                        ? "bg-blue-500/20 text-blue-400 border border-blue-500/50"
                        : "bg-zinc-800 text-zinc-400 border border-zinc-700"
                    }`}
                  >
                    <Code className="w-3 h-3" />
                    HTML Code
                  </button>
                </div>
                
                {/* Visual Builder Toolbar for Edit */}
                {editMode === "visual" && (
                  <div className="flex flex-col gap-2 mb-2 p-2 bg-zinc-800/50 border border-zinc-700">
                    <div className="flex items-center gap-2">
                      <button className="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                        <Type className="w-3 h-3" /> Text
                      </button>
                      <button className="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                        <Image className="w-3 h-3" /> Image
                      </button>
                      <button 
                        onClick={() => setShowVideoInput(!showVideoInput)}
                        className={`flex items-center gap-1 px-2 py-1 text-xs font-mono ${showVideoInput ? 'bg-red-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                      >
                        <Video className="w-3 h-3" /> Video
                      </button>
                      <button className="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                        <MousePointer className="w-3 h-3" /> Button
                      </button>
                      <button className="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                        <Gift className="w-3 h-3" /> Promo Box
                      </button>
                      <button className="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                        <List className="w-3 h-3" /> Bullet List
                      </button>
                      <button className="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-zinc-700 text-zinc-300 hover:bg-zinc-600">
                        <Minus className="w-3 h-3" /> Divider
                      </button>
                    </div>
                    
                    {/* Video URL Input */}
                    {showVideoInput && (
                      <div className="flex items-center gap-2 p-2 bg-red-900/20 border border-red-500/30">
                        <Video className="w-4 h-4 text-red-400" />
                        <input
                          type="text"
                          value={videoUrl}
                          onChange={(e) => setVideoUrl(e.target.value)}
                          placeholder="Paste YouTube URL (e.g., https://youtube.com/watch?v=...)"
                          className="flex-1 px-2 py-1 bg-zinc-900 border border-zinc-700 text-white text-xs font-mono focus:outline-none focus:border-red-500"
                        />
                        <button
                          onClick={insertVideoThumbnail}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-mono"
                        >
                          Insert Thumbnail
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                <textarea
                  value={formData.html_content}
                  onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                  placeholder="<html><body><p>Hello {business_name},</p></body></html>"
                  className="w-full h-64 p-3 bg-zinc-900 border border-zinc-700 text-white font-mono text-sm resize-none focus:outline-none focus:border-blue-500"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleUpdateTemplate}
                  disabled={isLoading}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-mono rounded-none"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Update Template
                </Button>
                <Button
                  onClick={() => {
                    setEditingTemplate(null);
                    setFormData({ name: "", description: "", subject: "", html_content: "" });
                  }}
                  variant="outline"
                  className="rounded-none border-zinc-700 font-mono"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          
          {/* Drafts Tab */}
          {activeTab === "drafts" && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 font-mono">No drafts yet.</p>
            </div>
          )}
          
          {/* Results Tab */}
          {activeTab === "results" && (
            <ResultsTab campaigns={campaigns} selectedProject={selectedProject} />
          )}
          
          {/* Locations Tab */}
          {activeTab === "locations" && (
            <LocationsTab />
          )}
        </div>
      </div>
    </div>
  );
};

// Project Stats Bar - shows total emails sent for the selected project
const ProjectStatsBar = ({ leadsWithEmail, businesses, selectedProject, activeTab, onNewTemplate }) => {
  const [projectStats, setProjectStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      setIsLoading(true);
      try {
        const params = selectedProject ? `?project=${selectedProject}` : '';
        const response = await axios.get(`${API}/total-email-stats${params}`);
        setProjectStats(response.data);
      } catch (error) {
        console.error("Failed to fetch project stats:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, [selectedProject]);

  return (
    <div className="mb-4 space-y-2">
      {/* Total Emails Sent Banner */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3 bg-gradient-to-r from-zinc-800/80 to-zinc-900 border border-zinc-700">
        {isLoading ? (
          <div className="col-span-5 flex items-center justify-center py-2">
            <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
            <span className="ml-2 text-zinc-400 text-xs">Loading stats...</span>
          </div>
        ) : projectStats ? (
          <>
            <div className="text-center p-2 bg-zinc-900/50 border border-zinc-700">
              <div className="text-lg font-mono font-bold text-white" data-testid="total-sent-count">{projectStats.total_sent}</div>
              <div className="text-xs text-zinc-500">Total Sent</div>
            </div>
            <div className="text-center p-2 bg-zinc-900/50 border border-green-500/20">
              <div className="text-lg font-mono font-bold text-green-400">{projectStats.delivered}</div>
              <div className="text-xs text-zinc-500">Delivered</div>
            </div>
            <div className="text-center p-2 bg-zinc-900/50 border border-blue-500/20">
              <div className="text-lg font-mono font-bold text-blue-400">{projectStats.opened}</div>
              <div className="text-xs text-zinc-500">Opened</div>
            </div>
            <div className="text-center p-2 bg-zinc-900/50 border border-yellow-500/20">
              <div className="text-lg font-mono font-bold text-yellow-400">{projectStats.clicked}</div>
              <div className="text-xs text-zinc-500">Clicked</div>
            </div>
            <div className="text-center p-2 bg-zinc-900/50 border border-red-500/20">
              <div className="text-lg font-mono font-bold text-red-400">{projectStats.bounced}</div>
              <div className="text-xs text-zinc-500">Bounced</div>
            </div>
          </>
        ) : (
          <div className="col-span-5 text-center text-zinc-500 text-xs py-2">Could not load stats</div>
        )}
      </div>
      
      {/* Leads info row */}
      <div className="flex items-center justify-between p-3 bg-zinc-800/50 border border-zinc-700">
        <div>
          <span className="text-green-400 font-mono font-bold">{leadsWithEmail.length}</span>
          <span className="text-zinc-400 text-sm ml-2">leads with email addresses ready</span>
          <div className="text-xs text-zinc-600">
            From {businesses.length} total leads
          </div>
        </div>
        {activeTab === "templates" && (
          <Button
            onClick={onNewTemplate}
            className="bg-green-500 hover:bg-green-600 text-black font-mono text-xs rounded-none"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
        )}
      </div>
    </div>
  );
};

// Results Tab Component with Analytics
const ResultsTab = ({ campaigns, selectedProject }) => {
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [engagedEmails, setEngagedEmails] = useState(null);
  const [isLoadingEngaged, setIsLoadingEngaged] = useState(true);
  
  // Follow-up state
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpEmails, setFollowUpEmails] = useState([]);
  const [followUpFilter, setFollowUpFilter] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [senderName, setSenderName] = useState("");
  const [senderCompany, setSenderCompany] = useState("");
  const [isSendingFollowUp, setIsSendingFollowUp] = useState(false);

  useEffect(() => {
    fetchEngagedEmails();
  }, [selectedProject]);

  const fetchEngagedEmails = async () => {
    setIsLoadingEngaged(true);
    try {
      const params = selectedProject ? `?project=${selectedProject}` : '';
      const response = await axios.get(`${API}/total-email-stats${params}`);
      setEngagedEmails(response.data.emails || []);
    } catch (error) {
      console.error("Failed to fetch engaged emails:", error);
      setEngagedEmails([]);
    } finally {
      setIsLoadingEngaged(false);
    }
  };

  const handleSendFollowUp = async (emails, filterType) => {
    setFollowUpEmails(emails);
    setFollowUpFilter(filterType);
    // Fetch templates
    try {
      const res = await axios.get(`${API}/templates`);
      setTemplates(res.data);
    } catch (e) {
      console.error("Failed to fetch templates:", e);
    }
    setShowFollowUp(true);
  };

  const sendFollowUp = async () => {
    if (!selectedTemplate) {
      toast.error("Please select a template");
      return;
    }
    if (!senderName || !senderCompany) {
      toast.error("Please enter your name and company");
      return;
    }

    const businesses = followUpEmails.map(email => ({
      id: email,
      name: email.split("@")[0],
      email: email,
      address: "",
      category: "",
      website: ""
    }));

    setIsSendingFollowUp(true);
    try {
      // Check duplicates first
      const checkPayload = {
        template_id: selectedTemplate.id,
        businesses,
        sender_name: senderName,
        sender_company: senderCompany,
        sender_email: PROJECTS_MAP[selectedProject] || "",
        project: selectedProject
      };
      
      const checkResponse = await axios.post(`${API}/email/check-duplicates`, checkPayload);
      const { new_count, duplicate_count, total } = checkResponse.data;
      
      let confirmMsg;
      if (new_count === 0) {
        toast.error(`All ${total} follow-up emails have already been sent with this template.`);
        setIsSendingFollowUp(false);
        return;
      } else if (duplicate_count > 0) {
        confirmMsg = `${duplicate_count} already sent. Send follow-up to ${new_count} new recipients?`;
      } else {
        confirmMsg = `Send follow-up to ${new_count} recipients?`;
      }

      if (!confirm(confirmMsg)) {
        setIsSendingFollowUp(false);
        return;
      }

      const response = await axios.post(`${API}/email/send-bulk`, checkPayload);
      let message = `Follow-up sent to ${response.data.successful} recipients!`;
      if (response.data.duplicates_skipped > 0) {
        message += ` (${response.data.duplicates_skipped} duplicates skipped)`;
      }
      toast.success(message);
      setShowFollowUp(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send follow-up");
    } finally {
      setIsSendingFollowUp(false);
    }
  };

  // Simple project-to-sender mapping
  const PROJECTS_MAP = {
    hadfun: "info@hadfun.co.uk",
    cafeops: "support@cafeops.co.uk"
  };

  const fetchAnalytics = async (campaignId) => {
    setIsLoadingAnalytics(true);
    try {
      const response = await axios.get(`${API}/campaign-analytics/${campaignId}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  const handleCampaignClick = (campaign) => {
    setSelectedCampaign(campaign);
    fetchAnalytics(campaign.id);
  };

  if (campaigns.length === 0) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
        <p className="text-zinc-500 font-mono">No campaigns sent yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Engaged Leads Section */}
      <div className="p-4 bg-gradient-to-r from-zinc-800/80 to-zinc-900 border border-zinc-700">
        <h4 className="font-mono text-sm text-cyan-400 uppercase mb-3 flex items-center gap-2">
          <Eye className="w-4 h-4" />
          {selectedProject ? `${selectedProject.toUpperCase()} Email Recipients` : 'All Email Recipients'}
        </h4>
        
        {isLoadingEngaged ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
            <span className="ml-2 text-zinc-400 text-sm">Loading email data...</span>
          </div>
        ) : engagedEmails && engagedEmails.length > 0 ? (
          <EngagedLeads emails={engagedEmails} selectedProject={selectedProject} onSendFollowUp={handleSendFollowUp} />
        ) : (
          <p className="text-zinc-500 text-sm">No email data available</p>
        )}
      </div>

      {/* Campaign List */}
      {campaigns.length === 0 ? (
        <div className="text-center py-8">
          <BarChart3 className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 font-mono text-sm">No campaigns in Searchalead yet.</p>
          <p className="text-zinc-600 text-xs mt-1">Send a campaign to see detailed analytics here.</p>
        </div>
      ) : (
        <>
          <h4 className="font-mono text-sm text-zinc-500 uppercase">Campaigns from Searchalead</h4>
          <div className="space-y-3">
            {campaigns.map((campaign) => (
              <div 
                key={campaign.id} 
                className={`p-4 border cursor-pointer transition-colors ${
                  selectedCampaign?.id === campaign.id 
                    ? "border-green-500 bg-green-500/10" 
                    : "border-zinc-700 bg-zinc-800/30 hover:border-zinc-600"
                }`}
                onClick={() => handleCampaignClick(campaign)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-mono font-bold text-white">{campaign.template_name}</h3>
                      {campaign.project && (
                        <span className={`px-2 py-0.5 text-xs font-mono rounded ${
                          campaign.project === 'hadfun' ? 'bg-purple-500/20 text-purple-400' : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {campaign.project}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500">
                      {new Date(campaign.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-sm font-mono">
                    <div className="flex items-center gap-1 text-green-400">
                      <CheckCircle2 className="w-4 h-4" />
                      {campaign.successful} sent
                    </div>
                    {campaign.failed > 0 && (
                      <div className="flex items-center gap-1 text-red-400">
                        <XCircle className="w-4 h-4" />
                        {campaign.failed} failed
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Analytics Panel */}
      {selectedCampaign && (
        <div className="mt-6 p-4 bg-zinc-800/50 border border-zinc-700">
          <h4 className="font-mono text-sm text-green-500 uppercase mb-4">
            Campaign Analytics: {selectedCampaign.template_name}
          </h4>
          
          {isLoadingAnalytics ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-green-500" />
              <span className="ml-2 text-zinc-400">Loading analytics...</span>
            </div>
          ) : analytics ? (
            <div className="space-y-4">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="p-3 bg-zinc-900 border border-zinc-700 text-center">
                  <div className="text-2xl font-mono font-bold text-white">{analytics.total_sent}</div>
                  <div className="text-xs text-zinc-500 uppercase">Total Sent</div>
                </div>
                <div className="p-3 bg-zinc-900 border border-green-500/30 text-center">
                  <div className="text-2xl font-mono font-bold text-green-400">{analytics.delivered}</div>
                  <div className="text-xs text-zinc-500 uppercase">Delivered</div>
                </div>
                <div className="p-3 bg-zinc-900 border border-blue-500/30 text-center">
                  <div className="text-2xl font-mono font-bold text-blue-400">{analytics.opened}</div>
                  <div className="text-xs text-zinc-500 uppercase">Opened</div>
                </div>
                <div className="p-3 bg-zinc-900 border border-yellow-500/30 text-center">
                  <div className="text-2xl font-mono font-bold text-yellow-400">{analytics.clicked}</div>
                  <div className="text-xs text-zinc-500 uppercase">Clicked</div>
                </div>
                <div className="p-3 bg-zinc-900 border border-red-500/30 text-center">
                  <div className="text-2xl font-mono font-bold text-red-400">{analytics.bounced}</div>
                  <div className="text-xs text-zinc-500 uppercase">Bounced</div>
                </div>
              </div>

              {/* Open Rate / Click Rate */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-zinc-900 border border-zinc-700">
                  <div className="text-lg font-mono font-bold text-blue-400">
                    {analytics.delivered > 0 ? Math.round((analytics.opened / analytics.delivered) * 100) : 0}%
                  </div>
                  <div className="text-xs text-zinc-500">Open Rate</div>
                </div>
                <div className="p-3 bg-zinc-900 border border-zinc-700">
                  <div className="text-lg font-mono font-bold text-yellow-400">
                    {analytics.opened > 0 ? Math.round((analytics.clicked / analytics.opened) * 100) : 0}%
                  </div>
                  <div className="text-xs text-zinc-500">Click Rate</div>
                </div>
              </div>

              {/* Recipients List */}
              <div className="mt-4">
                <h5 className="font-mono text-xs text-zinc-500 uppercase mb-2">Recipients</h5>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {analytics.recipients.map((recipient, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-zinc-900/50 text-xs">
                      <div className="flex-1">
                        <span className="text-white">{recipient.business_name}</span>
                        {recipient.location && (
                          <span className="text-cyan-400 ml-2">📍 {recipient.location}</span>
                        )}
                        <span className="text-zinc-500 ml-2">{recipient.email}</span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-mono ${
                        recipient.last_event === 'clicked' ? 'bg-yellow-500/20 text-yellow-400' :
                        recipient.last_event === 'opened' ? 'bg-blue-500/20 text-blue-400' :
                        recipient.last_event === 'delivered' ? 'bg-green-500/20 text-green-400' :
                        recipient.last_event === 'bounced' ? 'bg-red-500/20 text-red-400' :
                        recipient.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                        'bg-zinc-700 text-zinc-400'
                      }`}>
                        {recipient.last_event || recipient.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Follow-Up Modal */}
      {showFollowUp && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
          <div className="bg-zinc-900 border border-cyan-500/30 w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <Send className="w-5 h-5 text-cyan-400" />
                <h2 className="text-lg font-mono font-bold text-white">Send Follow-Up</h2>
              </div>
              <button onClick={() => setShowFollowUp(false)} className="p-2 hover:bg-zinc-800">
                <X className="w-5 h-5 text-zinc-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="p-3 bg-cyan-500/5 border border-cyan-500/20">
                <p className="text-sm text-cyan-400 font-mono">
                  Sending to <span className="font-bold">{followUpEmails.length}</span> recipients who{" "}
                  {followUpFilter === "clicked" ? "clicked" : 
                   followUpFilter === "opened" || followUpFilter === "engaged" ? "opened/clicked" :
                   followUpFilter === "delivered" ? "received" : "were sent"}{" "}
                  your previous email
                </p>
              </div>

              <div>
                <label className="text-xs font-mono text-zinc-500 uppercase block mb-1">Template</label>
                <select
                  data-testid="followup-template-select"
                  value={selectedTemplate?.id || ""}
                  onChange={(e) => setSelectedTemplate(templates.find(t => t.id === e.target.value) || null)}
                  className="w-full p-2 bg-zinc-800 border border-zinc-700 text-white font-mono text-sm focus:outline-none focus:border-cyan-500"
                >
                  <option value="">Select a template...</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} — {t.subject}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-mono text-zinc-500 uppercase block mb-1">Your Name</label>
                  <input
                    data-testid="followup-sender-name"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    className="w-full p-2 bg-zinc-800 border border-zinc-700 text-white font-mono text-sm focus:outline-none focus:border-cyan-500"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="text-xs font-mono text-zinc-500 uppercase block mb-1">Company</label>
                  <input
                    data-testid="followup-sender-company"
                    value={senderCompany}
                    onChange={(e) => setSenderCompany(e.target.value)}
                    className="w-full p-2 bg-zinc-800 border border-zinc-700 text-white font-mono text-sm focus:outline-none focus:border-cyan-500"
                    placeholder="Company name"
                  />
                </div>
              </div>

              <div className="p-2 bg-zinc-800/50 border border-zinc-700">
                <p className="text-xs text-zinc-500 font-mono">
                  Sending from: <span className="text-green-400">{PROJECTS_MAP[selectedProject] || "default"}</span>
                  <span className="text-zinc-600 ml-2">({selectedProject})</span>
                </p>
              </div>

              <Button
                data-testid="send-followup-confirm"
                onClick={sendFollowUp}
                disabled={isSendingFollowUp || !selectedTemplate}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-mono font-bold rounded-none"
              >
                {isSendingFollowUp ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> SEND FOLLOW-UP TO {followUpEmails.length} RECIPIENTS</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Engaged Leads - shows opened/clicked emails for follow-up
const EngagedLeads = ({ emails, selectedProject, onSendFollowUp }) => {
  const [filter, setFilter] = useState("engaged");
  const [copiedAll, setCopiedAll] = useState(false);

  // Cumulative: clicking implies opening, opening implies delivering
  const clicked = emails.filter(e => e.last_event === "clicked");
  const opened = emails.filter(e => e.last_event === "opened" || e.last_event === "clicked");
  const engaged = opened; // same as opened (opened + clicked)
  const delivered = emails.filter(e => ["delivered", "opened", "clicked"].includes(e.last_event));
  const bounced = emails.filter(e => e.last_event === "bounced");

  const getFilteredEmails = () => {
    switch (filter) {
      case "clicked": return clicked;
      case "opened": return opened;
      case "engaged": return engaged;
      case "delivered": return delivered;
      case "bounced": return bounced;
      default: return emails;
    }
  };

  const filteredEmails = getFilteredEmails();

  const copyEmails = () => {
    const emailList = filteredEmails.map(e => e.to).filter(Boolean).join("\n");
    navigator.clipboard.writeText(emailList);
    setCopiedAll(true);
    toast.success(`Copied ${filteredEmails.length} email addresses`);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const handleFollowUp = () => {
    const followUpEmails = filteredEmails.map(e => e.to).filter(Boolean);
    if (onSendFollowUp) {
      onSendFollowUp(followUpEmails, filter);
    }
  };

  return (
    <div className="mt-4 space-y-3">
      {/* Filter buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-zinc-500 font-mono mr-1">FILTER:</span>
        {[
          { id: "engaged", label: "Opened + Clicked", count: engaged.length, color: "text-cyan-400 border-cyan-500/30" },
          { id: "clicked", label: "Clicked Only", count: clicked.length, color: "text-yellow-400 border-yellow-500/30" },
          { id: "opened", label: "Opened (incl. Clicked)", count: opened.length, color: "text-blue-400 border-blue-500/30" },
          { id: "delivered", label: "Delivered", count: delivered.length, color: "text-green-400 border-green-500/30" },
          { id: "bounced", label: "Bounced", count: bounced.length, color: "text-red-400 border-red-500/30" },
          { id: "all", label: "All", count: emails.length, color: "text-zinc-400 border-zinc-600" },
        ].map((f) => (
          <button
            key={f.id}
            data-testid={`filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`px-2 py-1 text-xs font-mono border transition-colors ${
              filter === f.id
                ? `${f.color} bg-zinc-800`
                : "text-zinc-600 border-zinc-800 hover:border-zinc-700"
            }`}
          >
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {/* Action bar */}
      {filteredEmails.length > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-zinc-500 font-mono">
            {filteredEmails.length} recipients
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleFollowUp}
              data-testid="send-followup-btn"
              className="px-4 py-1.5 text-xs font-mono font-bold bg-cyan-500 text-black hover:bg-cyan-400 transition-colors flex items-center gap-1.5"
            >
              <Send className="w-3 h-3" />
              SEND FOLLOW-UP ({filteredEmails.length})
            </button>
            <button
              onClick={copyEmails}
              data-testid="copy-filtered-emails"
              className="px-3 py-1.5 text-xs font-mono text-zinc-500 border border-zinc-800 hover:border-zinc-600 hover:text-zinc-300 transition-colors"
            >
              {copiedAll ? "COPIED!" : "COPY"}
            </button>
          </div>
        </div>
      )}

      {/* Email list */}
      <div className="max-h-64 overflow-y-auto space-y-1 bg-zinc-900/50 p-2 border border-zinc-800">
        {filteredEmails.length === 0 ? (
          <p className="text-zinc-600 text-xs text-center py-4">No emails match this filter</p>
        ) : (
          filteredEmails.map((email, idx) => (
            <div key={idx} className="flex items-center justify-between text-xs py-1.5 border-b border-zinc-800/50 last:border-0 hover:bg-zinc-800/30 transition-colors">
              <div className="flex-1 truncate">
                <span className="text-zinc-300 font-mono">{email.to}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-zinc-600">
                  {email.created_at ? new Date(email.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '-'}
                </span>
                <span className={`px-2 py-0.5 text-xs font-mono ${
                  email.last_event === 'clicked' ? 'bg-yellow-500/20 text-yellow-400' :
                  email.last_event === 'opened' ? 'bg-blue-500/20 text-blue-400' :
                  email.last_event === 'delivered' ? 'bg-green-500/20 text-green-400' :
                  email.last_event === 'bounced' ? 'bg-red-500/20 text-red-400' :
                  'bg-zinc-700 text-zinc-400'
                }`}>
                  {email.last_event === 'clicked' ? 'opened + clicked' :
                   email.last_event || 'sent'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Locations Tab - Shows all searched locations grouped by niche
const LocationsTab = () => {
  const [locationData, setLocationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/locations-summary`);
      setLocationData(response.data);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-green-500" />
        <span className="ml-2 text-zinc-400">Loading search history...</span>
      </div>
    );
  }

  if (!locationData || locationData.total_searches === 0) {
    return (
      <div className="text-center py-12">
        <MapPin className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
        <p className="text-zinc-500 font-mono">No searches recorded yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-3 bg-zinc-800/50 border border-zinc-700 flex items-center justify-between">
        <span className="text-zinc-400 text-sm font-mono">
          <span className="text-green-400 font-bold">{locationData.total_searches}</span> searches across{" "}
          <span className="text-green-400 font-bold">{Object.keys(locationData.by_niche).length}</span> niches
        </span>
      </div>

      {Object.entries(locationData.by_niche).map(([niche, locations]) => (
        <div key={niche} className="border border-zinc-700">
          <div className="p-3 bg-zinc-800/80 border-b border-zinc-700 flex items-center justify-between">
            <h4 className="font-mono font-bold text-white uppercase text-sm">{niche}</h4>
            <span className="text-xs text-zinc-500 font-mono">{locations.length} location{locations.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="divide-y divide-zinc-800">
            {locations.map((loc, idx) => (
              <div key={idx} className="px-3 py-2 flex items-center justify-between text-sm hover:bg-zinc-800/30 transition-colors">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3 h-3 text-cyan-400 flex-shrink-0" />
                  <span className="text-white font-mono">{loc.location}</span>
                </div>
                <div className="flex items-center gap-4 text-xs font-mono">
                  <span className="text-zinc-400">{loc.total_found} found</span>
                  <span className="text-orange-400">{loc.without_website} leads</span>
                  {loc.date && (
                    <span className="text-zinc-600">
                      {typeof loc.date === 'string' 
                        ? new Date(loc.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                        : ''}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BulkEmailCampaign;
